/* ClearProteinX theme scripts */

(function () {
  'use strict';

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    const nodes = Array.from(container.querySelectorAll(focusableSelector)).filter(
      (el) => el.offsetParent !== null
    );
    if (!nodes.length) return () => {};
    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return () => container.removeEventListener('keydown', onKeydown);
  }

  function formatMoney(cents) {
    const format = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    const value = (cents / 100).toFixed(2);
    return format ? `${value} ${format}` : `$${value}`;
  }

  /* ------------------------------------------------------------------ */
  /* Drawer (shared by menu + cart)                                      */
  /* ------------------------------------------------------------------ */

  class Drawer extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.addEventListener('click', (event) => {
        if (
          event.target.classList.contains('drawer__overlay') ||
          event.target.closest('[data-drawer-close]')
        ) {
          this.close();
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('scroll-lock');
      requestAnimationFrame(() => {
        const panel = this.querySelector('.drawer__panel');
        if (panel) this.releaseFocus = trapFocus(panel);
      });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('scroll-lock');
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener) this.opener.focus();
    }

    toggle(opener) {
      this.classList.contains('is-open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('drawer-element', Drawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.querySelector(opener.getAttribute('data-drawer-open'));
    if (!drawer) return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */

  const Cart = {
    sectionsToRender() {
      const ids = ['cart-drawer', 'cart-icon-bubble'];
      if (document.getElementById('main-cart')) ids.push('main-cart');
      return ids;
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const target = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
        if (!target) return;
        const html = new DOMParser().parseFromString(sections[id], 'text/html');
        const source =
          html.getElementById(`shopify-section-${id}`) || html.getElementById(id) || html.body;
        if (source) target.innerHTML = source.innerHTML;
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: this.sectionsToRender() }, body))
      });
      const data = await response.json();
      if (!response.ok) throw data;
      return data;
    },

    async add(formData) {
      const body = {};
      formData.forEach((value, key) => {
        body[key] = value;
      });
      body.sections = this.sectionsToRender();
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      this.renderSections(data.sections);
      return data;
    },

    async change(line, quantity) {
      const data = await this.request(window.routes.cart_change_url, { line, quantity });
      this.renderSections(data.sections);
      return data;
    }
  };

  window.CPXCart = Cart;

  /* ------------------------------------------------------------------ */
  /* Product form                                                        */
  /* ------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('.product-form__error');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      if (this.dataset.ajax === 'false') return;
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.hideError();

      const formData = new FormData(this.form);
      // UI-only control, not a cart parameter.
      formData.delete('purchase-option');

      try {
        await Cart.add(formData);
        const drawer = document.querySelector('#CartDrawer');
        if (drawer && document.body.classList.contains('cart-drawer-enabled')) {
          drawer.open(this.submitButton);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (error) {
        this.showError(error.description || error.message || window.cartStrings.error);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      this.submitButton.classList.toggle('loading', state);
      this.submitButton.setAttribute('aria-busy', state);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-visible');
    }
  }

  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                      */
  /* ------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-id]');
      this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map(
        (input) => input.value
      );
    }

    onChange() {
      const selected = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((option, index) => option === selected[index])
      );

      this.updateAvailability(selected);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateURL(variant);
      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateButton(variant);

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    }

    updateAvailability(selected) {
      const groups = this.querySelectorAll('[data-option-index]');
      groups.forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((option, i) => (i === index ? option === input.value : option === candidate[i]))
          );
          input.disabled = !match || !match.available;
        });
      });
    }

    updateURL(variant) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    updateInput(variant) {
      const inputs = this.section.querySelectorAll('input[name="id"]');
      inputs.forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePrice(variant) {
      const target = this.section.querySelector('[data-price-wrapper]');
      if (!target) return;
      let html = `<span class="price__current">${formatMoney(variant.price)}</span>`;
      if (variant.compare_at_price > variant.price) {
        html += `<span class="price__regular">${formatMoney(variant.compare_at_price)}</span>`;
        target.classList.add('price--on-sale');
      } else {
        target.classList.remove('price--on-sale');
      }
      target.innerHTML = html;

      this.section.querySelectorAll('[data-subscription-price]').forEach((el) => {
        const percent = parseFloat(el.dataset.subscriptionPrice || '0');
        el.textContent = formatMoney(Math.round(variant.price * (1 - percent / 100)));
      });
      this.section.querySelectorAll('[data-onetime-price]').forEach((el) => {
        el.textContent = formatMoney(variant.price);
      });
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = this.section.querySelector('product-gallery');
      if (gallery) gallery.showMedia(variant.featured_media.id);
    }

    updateButton(variant) {
      this.section.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('.button__label') || button;
        if (variant.available) {
          button.removeAttribute('disabled');
          button.setAttribute('aria-disabled', 'false');
          label.textContent = button.dataset.addLabel || 'Add to cart';
        } else {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          label.textContent = button.dataset.soldOutLabel || 'Sold out';
        }
      });
    }

    setUnavailable() {
      this.section.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('.button__label') || button;
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
        label.textContent = button.dataset.unavailableLabel || 'Unavailable';
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------ */
  /* Product gallery                                                     */
  /* ------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
      });
    }

    showMedia(mediaId) {
      const id = String(mediaId);
      this.querySelectorAll('[data-media-id]').forEach((media) => {
        media.hidden = media.dataset.mediaId !== id;
      });
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        thumb.setAttribute('aria-current', thumb.dataset.mediaId === id ? 'true' : 'false');
      });
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ */
  /* Purchase options (one-time vs subscribe & save)                     */
  /* ------------------------------------------------------------------ */

  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.planInput = this.querySelector('input[name="selling_plan"]');
      this.planSelect = this.querySelector('[data-plan-select]');
      this.addEventListener('change', this.onChange.bind(this));
      this.onChange();
    }

    onChange() {
      const mode = this.querySelector('input[name="purchase-option"]:checked');

      // Mirrors the `:has(input:checked)` styling for browsers without :has support.
      this.querySelectorAll('.purchase-option').forEach((option) => {
        option.classList.toggle('is-selected', option.contains(mode));
      });

      if (!mode || !this.planInput) return;
      const subscribing = mode.value === 'subscription';
      if (this.planSelect) this.planSelect.hidden = !subscribing;
      this.planInput.value = subscribing
        ? this.planSelect
          ? this.planSelect.value
          : mode.dataset.sellingPlan || ''
        : '';
      this.planInput.disabled = !subscribing;
    }
  }

  customElements.define('purchase-options', PurchaseOptions);

  /* ------------------------------------------------------------------ */
  /* Quantity input                                                      */
  /* ------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.name === 'plus' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ */
  /* Cart line items                                                     */
  /* ------------------------------------------------------------------ */

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const line = input.dataset.line;
    try {
      await Cart.change(line, input.value);
    } catch (error) {
      console.error(error);
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    try {
      await Cart.change(remove.dataset.line, 0);
    } catch (error) {
      console.error(error);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Sticky add to cart                                                  */
  /* ------------------------------------------------------------------ */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.anchor || '[data-sticky-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------ */
  /* Rotating announcement bar                                           */
  /* ------------------------------------------------------------------ */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      if (this.slides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let index = 0;
      this.timer = setInterval(() => {
        this.slides[index].classList.remove('is-active');
        index = (index + 1) % this.slides.length;
        this.slides[index].classList.add('is-active');
      }, parseInt(this.dataset.speed || '4500', 10));
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------ */
  /* Misc                                                                */
  /* ------------------------------------------------------------------ */

  // Collection sort / filter auto-submit
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  // Accordions: only one FAQ item open at a time within a group
  document.addEventListener('toggle', (event) => {
    const item = event.target;
    if (!item.matches('.faq__item[open]')) return;
    const group = item.closest('[data-accordion-exclusive]');
    if (!group) return;
    group.querySelectorAll('.faq__item[open]').forEach((other) => {
      if (other !== item) other.open = false;
    });
  }, true);
})();
